import { Timestamp } from "firebase/firestore";
import { z } from "zod";

// Mirrors the bounds the SAVE_FEEDBACK_FORM service enforces. The backend is
// the authority; these exist so the editor can say no before a round trip.
export const LIMITS = {
  title: 200,
  intro: 2000,
  questions: 30,
  questionLabel: 300,
  optionLabel: 200,
  minOptions: 2,
  maxOptions: 10,
  shortText: 200,
  longText: 2000,
} as const;

/** Anonymous results stay hidden until this many people have answered. */
export const MIN_ANONYMOUS_RESULTS = 3;

export const QUESTION_TYPES = [
  "rating",
  "single",
  "multiple",
  "short_text",
  "long_text",
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  rating: "Rating 1–5",
  single: "Single choice",
  multiple: "Multiple choice",
  short_text: "Short text",
  long_text: "Long text",
};

export function isChoiceType(type: QuestionType): boolean {
  return type === "single" || type === "multiple";
}

const optionSchema = z.object({ id: z.string(), label: z.string() });
export type FeedbackOption = z.infer<typeof optionSchema>;

export const questionSchema = z.object({
  id: z.string(),
  type: z.enum(QUESTION_TYPES),
  label: z.string(),
  required: z.boolean(),
  options: z.array(optionSchema).optional(),
});
export type FeedbackQuestion = z.infer<typeof questionSchema>;

const timestampOrNull = z.instanceof(Timestamp).nullable().optional();

export const feedbackFormSchema = z.object({
  id: z.string(),
  title: z.string(),
  intro: z.string().optional().default(""),
  status: z.enum(["open", "closed"]),
  opensAt: timestampOrNull,
  closesAt: timestampOrNull,
  anonymous: z.boolean(),
  questions: z.array(questionSchema),
  responseCount: z.number().int().optional().default(0),
  updatedAt: z.any().optional(),
});
export type FeedbackForm = z.infer<typeof feedbackFormSchema>;

/** Rating: 1..5; single: option id; multiple: option ids; text: string. */
export type Answer = number | string | string[];
export type Answers = Record<string, Answer>;

const answerSchema = z.union([z.number(), z.string(), z.array(z.string())]);

export const submissionSchema = z.object({
  uid: z.string().optional(),
  status: z.enum(["REQUESTED", "QUEUED", "SUCCEEDED", "FAILED"]),
  answers: z.record(z.string(), answerSchema).optional(),
  error: z.string().optional(),
  anonymous: z.boolean().optional(),
  submittedAt: z.any().optional(),
  processedAt: z.any().optional(),
});
export type Submission = z.infer<typeof submissionSchema>;

export const responseSchema = z.object({
  id: z.string(),
  uid: z.string().optional(),
  attendeeId: z.string().nullable().optional(),
  answers: z.record(z.string(), answerSchema),
  submittedAt: z.any().optional(),
  updatedAt: z.any().optional(),
});
export type FeedbackResponse = z.infer<typeof responseSchema>;
